import { ChallengeService, PartnerService, TaskService, UserService } from '@duck/api-definitions'
import axios, { type AxiosRequestConfig } from 'axios'

// Configure axios defaults to send credentials with all requests 🏴‍☠️⭐
axios.defaults.withCredentials = true

// For now, just use a simple baseOptions without Configuration wrapper
// The API client should accept baseOptions directly in constructor
const baseOptions = {
  withCredentials: true, // Essential for sending HTTP-only cookies! 🏴‍☠️⭐
  isJsonMime: () => false
}

const USER_SERVICE_BASE = '/api/users'

export const apis = {
  userService: new UserService.DefaultApi(baseOptions, USER_SERVICE_BASE),
  taskService: new TaskService.DefaultApi(baseOptions, `/api/tasks`),
  challengeService: new ChallengeService.DefaultApi(baseOptions, `/api/challenges`),
  partnerService: new PartnerService.DefaultApi(baseOptions, `/api/partners`)
}

/** Run assignment status migration (sent→active, draft→inactive). Uses same auth as other API calls. */
export async function runAssignmentStatusMigration(options: { trial?: boolean } = {}): Promise<{
  success: boolean
  stats: { sentToActive: number; draftToInactive: number; errors: number }
  message: string
}> {
  const { data } = await axios.post<{
    success: boolean
    stats: { sentToActive: number; draftToInactive: number; errors: number }
    message: string
  }>(`${USER_SERVICE_BASE}/migrations/assignment-status-to-sent-draft`, { trial: options.trial ?? false })
  return data
}

/** Add assignmentOnly badge to users with source publicCandidate/partnerCandidate and exactly one ongoing challenge. */
export async function runAssignmentOnlyBadgeMigration(options: { trial?: boolean } = {}): Promise<{
  success: boolean
  stats: {
    eligible: number
    updated: number
    skippedBadgeExists: number
    skippedNotOneOngoingChallenge: number
    errors: number
  }
  message: string
}> {
  const { data } = await axios.post<{
    success: boolean
    stats: {
      eligible: number
      updated: number
      skippedBadgeExists: number
      skippedNotOneOngoingChallenge: number
      errors: number
    }
    message: string
  }>(`${USER_SERVICE_BASE}/migrations/add-assignment-only-badge`, { trial: options.trial ?? false })
  return data
}

/** Set settings.releaseNotesLastReadDate to a date for all users. */
export async function runReleaseNotesLastReadDateMigration(options: {
  trial?: boolean
  date: string
}): Promise<{
  success: boolean
  stats: { updated: number; errors: number }
  message: string
}> {
  const { data } = await axios.post<{
    success: boolean
    stats: { updated: number; errors: number }
    message: string
  }>(`${USER_SERVICE_BASE}/migrations/set-release-notes-last-read-date`, {
    trial: options.trial ?? false,
    date: options.date,
  })
  return data
}

const CHALLENGE_SERVICE_BASE = '/api/challenges'

/** Extract tips from feedbackText into feedbackTips for non-assignment challenges. */
export async function runFeedbackTipsMigration(options: { trial?: boolean } = {}): Promise<{
  updated: number
  skipped: number
  total: number
}> {
  const { data } = await axios.post<{
    updated: number
    skipped: number
    total: number
  }>(`${CHALLENGE_SERVICE_BASE}/migrate-feedback-tips`, { trial: options.trial ?? false })
  return data
}

/** Recalculate user progress for all users in batches. */
export async function runRecalculateAllProgressMigration(options: { trial?: boolean } = {}): Promise<{
  success: boolean
  stats: { total: number; recalculated: number; errors: number }
  message: string
}> {
  const { data } = await axios.post<{
    success: boolean
    stats: { total: number; recalculated: number; errors: number }
    message: string
  }>(`${USER_SERVICE_BASE}/migrations/recalculate-all-progress`, { trial: options.trial ?? false })
  return data
}

/** Add each person's assignment-implied orgs to their managingOrganizationIds. */
export async function runBackfillPersonManagingOrgsMigration(options: { trial?: boolean } = {}): Promise<{
  success: boolean
  stats: { membershipPairs: number; personsScanned: number; personsUpdated: number; orgLinksAdded: number; personsNotFound: number; errors: number }
  message: string
  affectedUserIds: string[]
}> {
  const { data } = await axios.post<{
    success: boolean
    stats: { membershipPairs: number; personsScanned: number; personsUpdated: number; orgLinksAdded: number; personsNotFound: number; errors: number }
    message: string
    affectedUserIds: string[]
  }>(`${USER_SERVICE_BASE}/migrations/backfill-person-managing-orgs`, { trial: options.trial ?? false })
  return data
}

type RefreshResult = { user: UserService.User; accessToken: string }
type RefreshHandlers = {
  setAccessToken: (token: string | null) => void
  setUser: (user: UserService.User) => void
  logout: () => void
  clearAuthError?: () => void
  // Called once when a refresh fails (the session has truly expired — e.g. the refresh
  // cookie lapsed). Not called on intentional sign-out, which never goes through here.
  onSessionExpired?: () => void
}

// Single in-flight refresh shared by BOTH the reactive 401 interceptor and the
// proactive refresh timer. The server rotates AND revokes the old refresh token on
// every refresh, so two concurrent refreshes would make the second fail with
// "Invalid or revoked refresh token" and spuriously log the user out. Deduping here
// guarantees there is only ever one refresh in flight. 🏴‍☠️⭐
let refreshPromise: Promise<RefreshResult> | null = null
let refreshHandlers: RefreshHandlers | null = null

/**
 * Refresh the access token via the HTTP-only refresh cookie, deduped so concurrent
 * callers share one request. Updates the token/user atoms on success and logs out on
 * failure (handled internally). Rejects if the auth interceptor hasn't been set up yet.
 */
export const refreshAccessToken = (): Promise<RefreshResult> => {
  const handlers = refreshHandlers
  if (!handlers) {
    return Promise.reject(new Error('Auth interceptor not initialized'))
  }
  if (!refreshPromise) {
    refreshPromise = apis.userService
      .authRefreshPost()
      .then(({ data }) => {
        const { user, accessToken } = data
        handlers.setAccessToken(accessToken)
        handlers.setUser(user)
        handlers.clearAuthError?.()
        return { user, accessToken }
      })
      .catch((refreshError) => {
        handlers.logout()
        handlers.onSessionExpired?.()
        throw refreshError
      })
      .finally(() => {
        refreshPromise = null
      })
  }
  return refreshPromise
}

// Function to set up axios interceptors - to be called from React component with proper token access 🏴‍☠️⭐
export const setupAxiosInterceptor = (
  getAccessToken: () => string | null,
  setAccessToken: (token: string | null) => void,
  setUser: (user: UserService.User) => void,
  logout: () => void,
  clearAuthError?: () => void,
  onSessionExpired?: () => void
) => {
  type RetriableAxiosRequestConfig = AxiosRequestConfig & { _isRetry?: boolean }
  refreshHandlers = { setAccessToken, setUser, logout, clearAuthError, onSessionExpired }

  // Add request interceptor with proper token access ⭐🏴‍☠️
  const requestInterceptorId = axios.interceptors.request.use((config) => {
    const accessToken = getAccessToken()
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }
    return config
  })

  // Add response interceptor for automatic session refresh 🏴‍☠️⭐
  const responseInterceptorId = axios.interceptors.response.use(
    // Success responses pass through unchanged
    (response) => response,
    // Handle error responses
    async (error) => {
      const originalRequest = error.config as RetriableAxiosRequestConfig
      
      // Check if this is a 401 error with "Session validation failed" message
      if (
        error.response?.status === 401 && 
        error.response?.data?.error === "Session validation failed" &&
        !originalRequest._isRetry // Prevent infinite retry loops
      ) {
        try {
          // Mark this request as a retry attempt
          originalRequest._isRetry = true

          const { accessToken } = await refreshAccessToken()

          // Update the authorization header for the original request
          if (originalRequest.headers) {
            if ('set' in originalRequest.headers && typeof originalRequest.headers.set === 'function') {
              originalRequest.headers.set('Authorization', `Bearer ${accessToken}`)
            } else {
              originalRequest.headers.Authorization = `Bearer ${accessToken}`
            }
          } else {
            originalRequest.headers = { Authorization: `Bearer ${accessToken}` }
          }

          // Retry the original request with the new token
          return axios(originalRequest)
          
        } catch {
          // Return the original error so components can handle the failed auth state
          return Promise.reject(error)
        }
      }
      
      // For all other errors, return as-is
      return Promise.reject(error)
    }
  )

  return () => {
    axios.interceptors.request.eject(requestInterceptorId)
    axios.interceptors.response.eject(responseInterceptorId)
    refreshHandlers = null
  }
}
